import React, { useState, useEffect, useRef } from "react";
import FixedBottomCard from "../fixed-bottom-card";
import PopupBottomModal from "../../../../components/popup-bottom-modal";
import EmptyUsers from "../../../../components/empty-users";
import { QueryRankingPage, QueryRecentCycleList } from "../../api";
import { Picker } from "@xp-design/mobile";
import { getPrizeText, formateDate } from "../../../../utils/common";
import { getUserRankingPrize } from "../../utils";
import {
  PrizeShowInfo,
  RankingUser,
  RankingApiResponse,
  CycleItem,
  InviteRankingProps
} from "../../types";
import "./index.less";

const InviteRanking: React.FC<InviteRankingProps> = ({
  activityCode
}) => {
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedCycleCode, setSelectedCycleCode] = useState<string>("");
  const [rankingData, setRankingData] = useState<RankingApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [cycleList, setCycleList] = useState<CycleItem[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  // 奖励详情弹窗状态
  const [showRewardDetail, setShowRewardDetail] = useState(false);
  const [selectedUser, setSelectedUser] = useState<RankingUser | null>(null);
  // 分页相关状态
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const isRequesting = useRef(false);

  // 获取用户等级名称 - 根据接口规则重写
  const getUserLevelName = (cycleSuccess: number, cycleRanking: number | null): any => {
    if (!rankingData?.rankGradeList) return null;

    // 如果有明确排名，优先根据排名确定档位
    if (cycleRanking !== null) {
      for (const grade of rankingData.rankGradeList) {
        // maxRanking 为 null 表示不限制最大排名
        if (grade.maxRanking === null) {
          if (cycleRanking >= grade.minRanking) {
            return grade.prizeShowInfoList?.[0];
          }
        } else {
          if (cycleRanking >= grade.minRanking && cycleRanking <= grade.maxRanking) {
            return grade.prizeShowInfoList?.[0];
          }
        }
      }
    }

    // 如果没有排名，根据成功数判断是否达到最低档位
    for (const grade of rankingData.rankGradeList) {
      if (cycleSuccess >= grade.prizeThreshold) {
        return grade.prizeShowInfoList?.[0];
      }
    }

    return null;
  };

  // 获取排名显示文本 - 按照接口规则
  const getRankDisplay = (user: RankingUser): string => {
    // 用户排名为 null 且 任务成功数 > 0 时，为有排名未上榜，排名展示：${maxRanking}+
    if (user.cycleRanking === null && user.cycleSuccess > 0) {
      const maxRank = rankingData?.maxRanking || 999;
      // 如果maxRanking超过9999，显示9999+
      if (maxRank > 9999) {
        return "9999+";
      }
      return `${maxRank}+`;
    }
    // 用户排名为 null 且 任务成功数 <= 0 时，为无排名，排名展示：-（横杠）
    if (user.cycleRanking === null && user.cycleSuccess <= 0) {
      return "-";
    }
    // 有明确排名时
    if (user.cycleRanking !== null) {
      // 如果排名超过9999，显示9999+
      if (user.cycleRanking > 9999) {
        return "9999+";
      }
      return user.cycleRanking.toString();
    }
    return "-";
  };

  // 判断是否显示排名徽章样式
  const getRankBadgeClass = (user: RankingUser) => {
    // 只有明确排名且在前三名时才显示特殊徽章
    if (user.cycleRanking === null) return "normal";

    if (user.cycleRanking === 1) return "top1";
    if (user.cycleRanking === 2) return "top2";
    if (user.cycleRanking === 3) return "top3";
    return "normal";
  };

  // 模拟API调用 - 更新为使用真实接口数据
  const fetchRankingData = async (cycleCode?: string, isLoadMore = false) => {
    if (isRequesting.current) return;

    const currentPageNo = isLoadMore ? pageNo : 1;

    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
      setPageNo(1);
      setHasMore(true);
    }

    isRequesting.current = true;

    try {
      // 调用真实接口，传入完整参数
      const response = await QueryRankingPage({
        activityType: "IR",
        activityCode: activityCode || "",
        cycleCode: cycleCode || selectedCycleCode,
        pageNo: currentPageNo,
        pageSize: pageSize
      });

      console.log('获取排行榜数据 - 周期:', cycleCode || selectedCycleCode, '页码:', currentPageNo, '返回数据:', response);

      if ([0, 200].includes(response.code)) {
        const newData = response.data;

        if (isLoadMore && rankingData) {
          // 加载更多时，累加排行榜列表数据
          const updatedData = {
            ...newData,
            rankingPage: [...(rankingData.rankingPage || []), ...(newData.rankingPage || [])]
          };
          setRankingData(updatedData);
        } else {
          // 首次加载或刷新时，直接设置数据
          setRankingData(newData);
        }

        // 更新分页状态 - 根据返回的数据长度判断是否还有更多
        const hasMoreData = (newData.rankingPage?.length || 0) >= pageSize;
        setHasMore(hasMoreData);

        if (isLoadMore) {
          setPageNo(currentPageNo + 1);
        } else {
          setPageNo(2); // 下次加载更多从第2页开始
        }
      }
    } catch (error) {
      console.error('获取排行榜数据失败:', error);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      isRequesting.current = false;
    }
  };

  // 格式化时间范围显示
  const formatTimeRange = (cycleCode: string): string => {
    const cycle = cycleList.find(item => item.cycleCode === cycleCode);
    if (!cycle) return selectedMonth;

    const startTime = formateDate(cycle.startTime);
    const endTime = formateDate(cycle.endTime);
    return `${startTime} - ${endTime}`;
  };

  // 获取周期列表
  const fetchCycleList = async () => {
    try {
      // 调用真实接口，传入完整参数
      const res = await QueryRecentCycleList({
        activityType: "IR",
        activityCode: activityCode || ""
      });

      console.log('获取周期列表:', res);

      if ([0, 200].includes(res.code)) {
        setCycleList(res.data || []);
        // 设置默认选中周期：优先选择当前周期，如果没有当前周期则选择第一个周期
        if (!selectedCycleCode && res.data && res.data.length > 0) {
          const currentCycle = res.data.find(item => item.currentCycle);
          const defaultCycle = currentCycle || res.data[0];
          setSelectedCycleCode(defaultCycle.cycleCode);
        } else {
          setLoading(false)
        }
      }
    } catch (error) {
      console.error('获取周期列表失败:', error);
    }
  };

  // 格式化周期数据为Picker选项
  const formatCycleOptions = () => {
    return cycleList.map(cycle => ({
      label: formatTimeRange(cycle.cycleCode),
      value: cycle.cycleCode
    }));
  };

  // 判断当前选中的周期是否为当前周期
  const isCurrentCycle = () => {
    const selectedCycle = cycleList.find(item => item.cycleCode === selectedCycleCode);
    return selectedCycle?.currentCycle || false;
  };

  useEffect(() => {
    // 只有当activityCode存在时才调用接口
    if (activityCode) {
      fetchCycleList();
    }
  }, [activityCode]);

  useEffect(() => {
    if (selectedCycleCode && cycleList.length > 0 && activityCode) {
      setSelectedMonth(formatTimeRange(selectedCycleCode));
      // 切换周期时重新获取排行榜数据
      fetchRankingData(selectedCycleCode, false);
    }
  }, [selectedCycleCode, cycleList, activityCode]);


  const handleMonthChange = () => {
    console.log('选择月份', cycleList);
    setShowPicker(true);
  };

  // Picker确认选择
  const handlePickerConfirm = (value: any) => {
    console.log('Picker confirm value:', value);

    // 根据@xp-design/mobile的Picker组件API，value可能是数组
    const selectedCode = Array.isArray(value) ? value[0] : value;

    if (selectedCode && selectedCode !== selectedCycleCode) {
      setSelectedCycleCode(selectedCode);
      console.log('选择周期:', selectedCode, formatTimeRange(selectedCode));
      // 立即重新获取对应周期的排行榜数据
      if (activityCode) {
        fetchRankingData(selectedCode, false);
      }
    }
    setShowPicker(false);
  };

  // Picker取消选择
  const handlePickerCancel = () => {
    setShowPicker(false);
  };

  // 处理列表项点击 - 显示奖励详情
  const handleUserClick = (user: RankingUser) => {
    setSelectedUser(user);
    setShowRewardDetail(true);
  };

  // 关闭奖励详情弹窗
  const handleCloseRewardDetail = () => {
    setShowRewardDetail(false);
    setSelectedUser(null);
  };

  // 计算单任务奖励{A} - 处理多个奖励的情况
  const calculateBaseReward = (user: RankingUser): string => {
    if (!rankingData?.inviterPrizeShowInfoList || rankingData.inviterPrizeShowInfoList.length === 0) return "";

    const basePrizes = rankingData.inviterPrizeShowInfoList;

    // 检查是否所有奖品都是储值卡
    const allStorageCards = basePrizes.every(basePrize => {
      // 判断是否为储值卡：prizeType为2，或者prizeType为11且pqType为"07"
      const isStorageCard = basePrize.prizeType === 2 ||
        (basePrize.prizeType === 11 && basePrize.prizeAttribute?.pqType === "07");
      return isStorageCard && basePrize.prizeAttribute?.pqAmt;
    });

    if (allStorageCards) {
      // 所有都是储值卡，计算总金额
      const totalAmount = basePrizes.reduce((sum, basePrize) => {
        const storageAmount = parseInt(basePrize.prizeAttribute.pqAmt) || 0; // 储值面额
        const successCount = user.cycleSuccess; // 本期邀请成功人数
        const prizeQuantity = parseInt(basePrize.prizeValue) || 1; // 奖品数量
        return sum + (storageAmount * successCount * prizeQuantity);
      }, 0);
      return totalAmount ? `${totalAmount}元` : '';
    } else {
      // 不全是储值卡，显示总份数
      const totalQuantity = basePrizes.reduce((sum, basePrize) => {
        // 积分算1份
        const prizeQuantity = basePrize.prizeType === 8 ? 1 : (parseInt(basePrize.prizeValue) || 1); // 奖品数量
        return sum + (prizeQuantity * user.cycleSuccess); // 奖品数量 * 邀请成功人数
      }, 0);
      return totalQuantity ? `${totalQuantity}份` : '';
    }
  };

  // 计算周期阶梯奖励{B} - 更新精确判断储值卡类型
  const calculateStepReward = (user: RankingUser): string => {
    if (!rankingData?.activityNodeList || rankingData.activityNodeList.length === 0) {
      return "";
    }

    // 找出已解锁的节点
    const unlockedNodes = rankingData.activityNodeList.filter(node =>
      user.cycleSuccess >= node.achieveThreshold
    ).sort((a, b) => a.achieveThreshold - b.achieveThreshold);

    if (unlockedNodes.length === 0) return "";

    // 检查是否每个节点均只有1个奖品且奖品类型为储值卡
    const allStorageCards = unlockedNodes.every(node =>
      node.prizeShowInfoList.length === 1 &&
      (() => {
        const prize = node.prizeShowInfoList[0];
        // 判断是否为储值卡：prizeType为2，或者prizeType为11且pqType为"07"
        const isStorageCard = prize.prizeType === 2 ||
          (prize.prizeType === 11 && prize.prizeAttribute?.pqType === "07");
        return isStorageCard && prize.prizeAttribute?.pqAmt;
      })()
    );

    if (allStorageCards) {
      // 计算总储值金额
      const totalAmount = unlockedNodes.reduce((sum, node) => {
        const prize = node.prizeShowInfoList[0];
        const storageAmount = parseInt(prize.prizeAttribute?.pqAmt || '0') || 0;
        const prizeQuantity = parseInt(prize.prizeValue) || 1;
        return sum + (storageAmount * prizeQuantity);
      }, 0);
      return `${totalAmount}元`;
    } else {
      // 显示已解锁的节点数，添加单位
      return unlockedNodes.length ? `${unlockedNodes.length}份` : '';
    }
  };

  // 格式化基础奖+阶梯奖显示
  const formatBaseAndStepReward = (user: RankingUser): string => {
    const baseReward = calculateBaseReward(user);
    const stepReward = calculateStepReward(user);

    const textArray: string[] = []
    !!baseReward && textArray.push(`基础奖${baseReward}`)
    !!stepReward && textArray.push(`阶梯奖${stepReward}`)
    return textArray.join(' | ')
  };

  // 获取用户奖励详情
  const getUserRewardDetail = (user: RankingUser) => {
    // 获取对应档位的奖励信息 - 修改逻辑：使用rankGradeList进行区间匹配
    let rankingPrize: PrizeShowInfo | null = null;
    if (user.cycleRanking !== null && rankingData?.rankGradeList && rankingData.rankGradeList.length > 0) {
      for (const grade of rankingData.rankGradeList) {
        // 检查排名是否落在当前档位区间内
        let isInRankRange = false;
        if (grade.maxRanking === null) {
          // maxRanking为null表示不限制最大排名，只要大于等于minRanking即可
          isInRankRange = user.cycleRanking >= grade.minRanking;
        } else {
          // 排名需要在minRanking和maxRanking之间
          isInRankRange = user.cycleRanking >= grade.minRanking && user.cycleRanking <= grade.maxRanking;
        }

        // 同时满足排名区间和成功数门槛条件
        if (isInRankRange && user.cycleSuccess >= grade.prizeThreshold) {
          rankingPrize = grade.prizeShowInfoList?.[0] || null;
          break; // 找到匹配的档位后立即退出
        }
      }
    }

    // 1. 单任务奖励文案：奖品名称1*（奖品数量*邀请成功人数）、奖品名称2*（奖品数量*邀请成功人数）...
    const formatBaseRewardDetail = () => {
      if (!rankingData?.inviterPrizeShowInfoList || rankingData.inviterPrizeShowInfoList.length === 0) {
        return '';
      }

      const baseRewards: string[] = [];

      rankingData.inviterPrizeShowInfoList.forEach(basePrize => {
        const prizeText = getPrizeText(basePrize) || basePrize.prizeName;
        const prizeQuantity = basePrize.prizeType === 8 ? 1 : (parseInt(basePrize.prizeValue) || 1); // 奖品数量
        const totalQuantity = prizeQuantity * user.cycleSuccess; // 奖品数量 * 邀请成功人数

        totalQuantity && baseRewards.push(`${prizeText}${basePrize.prizeType !== 13 ? `*${totalQuantity}` : ''}`);
      });

      return baseRewards.join('、');
    };

    // 2. 周期阶梯奖励文案：已解锁节点1奖品名称1*奖品数量、已解锁节点1奖品名称2*奖品数量、已解锁节点2奖品名称1*奖品数量...
    const formatStepRewardDetail = () => {
      if (!rankingData?.activityNodeList || rankingData.activityNodeList.length === 0) {
        return null;
      }

      // 找出已解锁的节点
      const unlockedNodes = rankingData.activityNodeList.filter(node =>
        user.cycleSuccess >= node.achieveThreshold
      ).sort((a, b) => a.achieveThreshold - b.achieveThreshold);

      if (unlockedNodes.length === 0) return null;

      const stepRewards: string[] = [];

      // 遍历每个已解锁节点的所有奖品
      unlockedNodes.forEach(node => {
        node.prizeShowInfoList.forEach(prize => {
          const prizeText = getPrizeText(prize) || prize.prizeName;
          const prizeQuantity = prize.prizeType === 8 ? 1 : (parseInt(prize.prizeValue) || 1); // 直接使用奖品数量，不乘以邀请成功人数
          stepRewards.push(`${prizeText}${prize.prizeType !== 13 ? `*${prizeQuantity}` : ''}`);
        });
      });

      return stepRewards.length > 0 ? stepRewards.join('、') : null;
    };

    // 3. 周期排名奖励文案：排名奖励名称1*奖品数量、排名奖励名称2*奖品数量、排名奖励名称3*奖品数量...
    const formatRankingReward = () => {
      if (!rankingPrize || user.cycleRanking === null) return null;

      // 获取该档位的所有奖品
      const gradeInfo = rankingData?.rankGradeList?.find(grade => {
        let isInRankRange = false;
        if (grade.maxRanking === null) {
          // 安全检查 user.cycleRanking 不为 null
          if (user.cycleRanking !== null) {
            isInRankRange = user.cycleRanking >= grade.minRanking;
          }
        } else {
          // 安全检查 user.cycleRanking 不为 null
          if (user.cycleRanking !== null) {
            isInRankRange = user.cycleRanking >= grade.minRanking && user.cycleRanking <= grade.maxRanking;
          }
        }
        return isInRankRange && user.cycleSuccess >= grade.prizeThreshold;
      });

      if (!gradeInfo || !gradeInfo.prizeShowInfoList || gradeInfo.prizeShowInfoList.length === 0) {
        return null;
      }

      const rankingRewards: string[] = [];

      // 遍历该档位的所有奖品
      gradeInfo.prizeShowInfoList.forEach(prize => {
        const prizeText = getPrizeText(prize) || prize.prizeName;
        const prizeQuantity = prize.prizeType === 8 ? 1 : parseInt(prize.prizeValue) || 1; // 直接使用奖品数量
        rankingRewards.push(`${prizeText}${prize.prizeType !== 13 ? `*${prizeQuantity}` : ''}`);
      });

      return rankingRewards.length > 0 ? rankingRewards.join('、') : null;
    };

    const rewardDetail = {
      rankingReward: formatRankingReward(),
      baseReward: formatBaseRewardDetail(),
      stepReward: formatStepRewardDetail()
    };

    return rewardDetail;
  };

  // 处理列表滚动，实现加载更多
  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (isRequesting.current || !hasMore || isLoadingMore) return;

    const { target } = e;
    const element = target as HTMLDivElement;

    // 当滚动到底部附近时加载更多
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - 50) {
      fetchRankingData(selectedCycleCode, true);
    }
  };

  return (
    <div className="invite-ranking">
      {selectedCycleCode && cycleList.length > 0 && <div className="ranking-header">
        <div className="month-selector" onClick={handleMonthChange}>
          <span>{formatTimeRange(selectedCycleCode)}</span>
          <div className="arrow-down"></div>
        </div>
        {isCurrentCycle() && (
          <div className="title">本期结束后发排名奖励</div>
        )}
      </div>}

      <div className="ranking-list">
        <div className="list-header">
          <div className="rank-col">排名</div>
          <div className="user-col">账号</div>
          <div className="count-col">邀请好友数</div>
        </div>

        <div className="list" onScroll={handleListScroll}>
          {loading ? (
            <div className="loading-state">
              <div className="loading-icon"></div>
            </div>
          ) : rankingData?.rankingPage && rankingData.rankingPage.length > 0 ? (
            <>
              {rankingData.rankingPage.map((user, index) => {
                const rankDisplay = getRankDisplay(user);
                const userRankingPrize = getUserRankingPrize(user, rankingData);

                return (
                  <div key={`${user.nickName}-${index}`} className="list-item" onClick={() => handleUserClick(user)}>
                    {/* 前三名名次图片 */}
                    {user.cycleRanking && user.cycleRanking <= 3 && (
                      <img
                        className="rank-decoration"
                        src={`https://s.xiaopeng.com/xp-charge/charge/invite-new-guest/top${user.cycleRanking}.png`}
                        alt={`第${user.cycleRanking}名`}
                      />
                    )}

                    <div className="first-row">
                      <div className="rank-number">
                        {/* 前三名不显示文字排名，只显示装饰图片 */}
                        {!(user.cycleRanking && user.cycleRanking <= 3) && (
                          <div className={`rank-badge ${getRankBadgeClass(user)}`}>
                            {rankDisplay}
                          </div>
                        )}
                      </div>

                      <div className="user-info">
                        <img className="avatar" src={user.headImg || require("../../assets/default-head.svg")} alt="" />
                        <div className="nickname">{user.nickName}</div>
                      </div>

                      <div className="invite-count">
                        <div className="count-number">
                          {user.cycleSuccess}
                          <span className="unit">人</span>
                        </div>
                      </div>
                    </div>

                    <div className="second-row">
                      {userRankingPrize ?
                        <div className="reward-info reward-info-left">
                          <div className={`reward-icon ${isCurrentCycle() ? 'lock' : 'unlock'}`}></div>
                          <div className="reward-text">
                            排名奖: {getPrizeText(userRankingPrize) || userRankingPrize.prizeName}
                          </div>
                        </div> : <div/>
                      }
                      {formatBaseAndStepReward(user) && <div className="reward-info reward-info-right">
                        <div className="reward-icon unlock"></div>
                        <div className="reward-text">
                          {formatBaseAndStepReward(user)}
                        </div>
                        <div className="arrow-right"></div>
                      </div>}
                    </div>
                  </div>
                );
              })}

              {/* 加载更多指示器 */}
              {isLoadingMore && (
                <div className="loading-more">
                  <div className="loading-text">加载更多...</div>
                </div>
              )}

              {/* 没有更多数据提示 */}
              {!hasMore && rankingData.rankingPage.length > 0 && (
                <div className="no-more">
                  <div className="no-more-text">没有更多数据了</div>
                </div>
              )}
            </>
          ) : (
            <EmptyUsers emptyText="暂无排行数据" />
          )}
        </div>

        {rankingData?.myRanking && (
          <FixedBottomCard
            rank={getRankDisplay(rankingData.myRanking)}
            inviteCount={rankingData.myRanking.cycleSuccess}
            rewardType={getPrizeText(getUserRankingPrize(rankingData.myRanking, rankingData))}
            rewardText={formatBaseAndStepReward(rankingData.myRanking)}
            userInfo={rankingData.myRanking}
            getUserRewardDetail={getUserRewardDetail}
            rankingData={rankingData}
            isCurrentCycle={isCurrentCycle}
          />
        )}
      </div>

      {/* Picker弹窗 */}
      <Picker
        visible={showPicker}
        title="选择排行榜日期"
        columns={[formatCycleOptions()]}
        value={[selectedCycleCode]}
        onConfirm={handlePickerConfirm}
        onClose={handlePickerCancel}
        style={{
          "--item-height" : '84px',
        }}
        popupClassName={'single-picker'}
      />

      {/* 奖励详情弹窗 */}
      <PopupBottomModal
        visible={showRewardDetail}
        onClose={handleCloseRewardDetail}
        title="奖励详情"
      >
        <div className="reward-detail-content">
          {selectedUser && (
            <div>
              {!!getUserRewardDetail(selectedUser).rankingReward && (
                <div>排名奖励: {getUserRewardDetail(selectedUser).rankingReward}</div>
              )}
              {!!getUserRewardDetail(selectedUser).baseReward && <div>基础奖励: {getUserRewardDetail(selectedUser).baseReward}</div>}
              {!!getUserRewardDetail(selectedUser).stepReward && (
                <div>阶梯奖励: {getUserRewardDetail(selectedUser).stepReward}</div>
              )}
            </div>
          )}
        </div>
        <div className="reward-detail-button-container">
          <button
            className="confirm-button"
            onClick={handleCloseRewardDetail}
          >
            确定
          </button>
        </div>
      </PopupBottomModal>
    </div>
  );
};

export default InviteRanking;
